import { useState, FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '../services/client';
import extractApiError from '../utils/extractApiError';
import type { SiteContext, SiteType, CoverageType, CoverageHours, PatrolIntensity, PrimaryObjective, TopRisk, ServiceType } from '../types';

interface SiteContextFormProps {
  siteId: string;
  initialContext?: SiteContext | null;
  onSuccess?: (ctx: SiteContext) => void;
  onCancel?: () => void;
}

const SITE_TYPE_OPTIONS: { value: SiteType; label: string }[] = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'retail', label: 'Retail' },
  { value: 'construction', label: 'Construction' },
  { value: 'office', label: 'Office' },
  { value: 'yard', label: 'Yard' },
  { value: 'parking_lot', label: 'Parking Lot' },
  { value: 'other', label: 'Other' },
];

const COVERAGE_TYPE_OPTIONS: { value: CoverageType; label: string; description: string }[] = [
  { value: 'static', label: 'Static', description: 'Guard stays at a fixed post. Infrequent patrols.' },
  { value: 'patrol', label: 'Patrol', description: 'Guard moves continuously on planned routes. No fixed post.' },
  { value: 'hybrid', label: 'Hybrid', description: 'Primary post plus scheduled rounds. Most common.' },
];

// Layer 2 — Service model discriminator.  Mobile Patrol is the only one
// that currently drives obligation-based scoring; the others are
// placeholders for later sprints.
const SERVICE_TYPE_OPTIONS: { value: ServiceType; label: string; description: string }[] = [
  { value: 'standing_guard', label: 'Standing Guard', description: 'Guard is stationed at a fixed post for the shift.' },
  { value: 'mobile_patrol', label: 'Mobile Patrol', description: 'Guard visits the site on a schedule. Requires a patrol plan.' },
  { value: 'fire_watch', label: 'Fire Watch', description: 'Continuous fire-safety inspection.' },
  { value: 'access_control', label: 'Access Control', description: 'Guard controls building/gate entry.' },
  { value: 'lock_unlock', label: 'Lock / Unlock', description: 'Scheduled door checks at open/close.' },
  { value: 'concierge', label: 'Concierge / Lobby', description: 'Front-desk / lobby coverage.' },
  { value: 'hybrid', label: 'Hybrid', description: 'Combination of standing guard + patrol.' },
];

const COVERAGE_HOURS_OPTIONS: { value: CoverageHours; label: string }[] = [
  { value: '8h', label: '8 hours / day' },
  { value: '12h', label: '12 hours / day' },
  { value: '16h', label: '16 hours / day' },
  { value: '24h', label: '24 hours / day' },
];

const PATROL_INTENSITY_OPTIONS: { value: PatrolIntensity; label: string; description: string }[] = [
  { value: 'low', label: 'Low', description: 'Quiet location, infrequent rounds.' },
  { value: 'medium', label: 'Medium', description: 'Standard activity, regular rounds.' },
  { value: 'high', label: 'High', description: 'High-traffic or higher-risk. Frequent rounds.' },
];

const PRIMARY_OBJECTIVE_OPTIONS: { value: PrimaryObjective; label: string }[] = [
  { value: 'deterrence', label: 'Deterrence' },
  { value: 'access_control', label: 'Access Control' },
  { value: 'patrol_visibility', label: 'Patrol Visibility' },
  { value: 'customer_service', label: 'Customer Service' },
  { value: 'loss_prevention', label: 'Loss Prevention' },
  { value: 'safety_hazard_reporting', label: 'Safety Hazard Reporting' },
  { value: 'parking_traffic_control', label: 'Parking / Traffic Control' },
];

const TOP_RISK_OPTIONS: { value: TopRisk; label: string }[] = [
  { value: 'trespassing', label: 'Trespassing' },
  { value: 'theft', label: 'Theft' },
  { value: 'vandalism', label: 'Vandalism' },
  { value: 'loitering', label: 'Loitering' },
  { value: 'disturbances', label: 'Disturbances' },
  { value: 'access_control_failure', label: 'Access Control Failure' },
  { value: 'safety_hazards', label: 'Safety Hazards' },
  { value: 'parking_traffic_issues', label: 'Parking / Traffic Issues' },
];

export default function SiteContextForm({
  siteId,
  initialContext,
  onSuccess,
  onCancel,
}: SiteContextFormProps) {
  const queryClient = useQueryClient();
  const [siteType, setSiteType] = useState<SiteType | ''>(initialContext?.site_type ?? '');
  const [coverageType, setCoverageType] = useState<CoverageType | ''>(initialContext?.coverage_type ?? '');
  const [serviceType, setServiceType] = useState<ServiceType | ''>(initialContext?.service_type ?? '');
  const [coverageHours, setCoverageHours] = useState<CoverageHours | ''>(initialContext?.coverage_hours ?? '');
  const [patrolIntensity, setPatrolIntensity] = useState<PatrolIntensity | ''>(initialContext?.patrol_intensity ?? '');
  const [primaryObjective, setPrimaryObjective] = useState<PrimaryObjective[]>(initialContext?.primary_objective ?? []);
  const [topRisks, setTopRisks] = useState<TopRisk[]>(initialContext?.top_risks ?? []);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      clientApi.updateSiteContext(siteId, {
        site_type: siteType,
        coverage_type: coverageType,
        service_type: serviceType,
        coverage_hours: coverageHours,
        patrol_intensity: patrolIntensity,
        primary_objective: primaryObjective,
        top_risks: topRisks,
      }),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['client', 'sites', siteId] });
      queryClient.invalidateQueries({ queryKey: ['client', 'sites'] });
      onSuccess?.(resp.data as SiteContext);
    },
    onError: (err: unknown) => setError(extractApiError(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!siteType || !coverageType || !serviceType || !coverageHours || !patrolIntensity) {
      setError('Please fill in Site Type, Coverage Type, Service Type, Coverage Hours, and Patrol Intensity.');
      return;
    }
    mutation.mutate();
  };

  const toggleObjective = (val: PrimaryObjective) => {
    setPrimaryObjective((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  };
  const toggleRisk = (val: TopRisk) => {
    setTopRisks((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      {/* Site Type */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Site Type <span className="text-red-500">*</span></label>
        <select
          value={siteType}
          onChange={(e) => setSiteType(e.target.value as SiteType)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          required
        >
          <option value="">Select...</option>
          {SITE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Coverage Type */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Coverage Type <span className="text-red-500">*</span></label>
        <select
          value={coverageType}
          onChange={(e) => setCoverageType(e.target.value as CoverageType)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          required
        >
          <option value="">Select...</option>
          {COVERAGE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {coverageType && (
          <p className="mt-1 text-xs text-slate-500">
            {COVERAGE_TYPE_OPTIONS.find((o) => o.value === coverageType)?.description}
          </p>
        )}
      </div>

      {/* Service Type (Layer 2 — drives obligation-based scoring for
          mobile_patrol; other types are placeholders for later sprints) */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Service Type <span className="text-red-500">*</span>
        </label>
        <select
          value={serviceType}
          onChange={(e) => setServiceType(e.target.value as ServiceType)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          required
        >
          <option value="">Select...</option>
          {SERVICE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {serviceType && (
          <p className="mt-1 text-xs text-slate-500">
            {SERVICE_TYPE_OPTIONS.find((o) => o.value === serviceType)?.description}
          </p>
        )}
        {serviceType === 'mobile_patrol' && (
          <p className="mt-1 text-xs text-brand-700">
            A patrol plan card will appear on this site's detail page after
            you save this context.
          </p>
        )}
      </div>

      {/* Coverage Hours */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Coverage Hours <span className="text-red-500">*</span></label>
        <select
          value={coverageHours}
          onChange={(e) => setCoverageHours(e.target.value as CoverageHours)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          required
        >
          <option value="">Select...</option>
          {COVERAGE_HOURS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Patrol Intensity */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Patrol Intensity <span className="text-red-500">*</span></label>
        <select
          value={patrolIntensity}
          onChange={(e) => setPatrolIntensity(e.target.value as PatrolIntensity)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          required
        >
          <option value="">Select...</option>
          {PATROL_INTENSITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {patrolIntensity && (
          <p className="mt-1 text-xs text-slate-500">
            {PATROL_INTENSITY_OPTIONS.find((o) => o.value === patrolIntensity)?.description}
          </p>
        )}
      </div>

      {/* Disclaimer for the next two fields */}
      <div className="pt-4 border-t border-slate-200">
        <p className="text-xs text-slate-500 italic mb-3">
          The next two fields do not affect the score in this version. They help contextualize the site for reporting and may inform future scoring rules.
        </p>
      </div>

      {/* Primary Objective */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Primary Objective</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PRIMARY_OBJECTIVE_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={primaryObjective.includes(opt.value)}
                onChange={() => toggleObjective(opt.value)}
                className="rounded border-slate-300 text-brand-500 focus:ring-brand-500"
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Top Risks */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Top Risks</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TOP_RISK_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={topRisks.includes(opt.value)}
                onChange={() => toggleRisk(opt.value)}
                className="rounded border-slate-300 text-brand-500 focus:ring-brand-500"
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={
            mutation.isPending
            || !siteType
            || !coverageType
            || !serviceType
            || !coverageHours
            || !patrolIntensity
          }
          className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? 'Saving...' : 'Save Site Context'}
        </button>
      </div>
    </form>
  );
}
